import * as React from 'react'

import { Tooltip, } from '../../../../../Shared/index'
import useModalAccessibility from '../../../../../Shared/hooks/useModalAccessibility';

const closeIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/close.svg`;

// Caps a long student-name list to what fits above the viewport bottom (the rest collapse into
// "...and N more") so the tooltip never runs off-screen. Sized for the truncation layout, which
// separates each name with a double <br/><br/> (~22px line + two ~19px breaks); erring large just
// shows "...and N more" slightly sooner, it can never overflow.
const AVERAGE_TOOLTIP_ITEM_HEIGHT = 60;

interface ExistingPack {
  unitId: number,
  classroomName: string,
}

interface OverrideWarningModalProps {
  handleClickAssign: () => void,
  handleCloseModal: () => void,
  diagnosticName: string,
  assignedStudentNames: string[],
  existingPacks: ExistingPack[],
}

const editActivityPackHref = (unitId: number) => `/teachers/classrooms/activity_planner/units/${unitId}/students/edit`

const OverrideWarningModal = ({ handleClickAssign, handleCloseModal, diagnosticName, assignedStudentNames, existingPacks, }: OverrideWarningModalProps) => {
  const { modalRef } = useModalAccessibility(handleCloseModal);

  const multiplePacks = existingPacks.length > 1

  const assignedStudentCount = assignedStudentNames.length
  const isSingular = assignedStudentCount === 1
  const studentCountText = `${assignedStudentCount} student${isSingular ? '' : 's'}`
  const haveOrHas = isSingular ? 'has' : 'have'
  const studentCountElement = assignedStudentCount
    ? <Tooltip averageItemHeight={AVERAGE_TOOLTIP_ITEM_HEIGHT} tooltipText={assignedStudentNames} tooltipTriggerText={studentCountText} />
    : studentCountText

  return (
    <div className="modal-container override-warning-modal-container">
      <div className="modal-background" />
      <div
        aria-labelledby="assignDiagnosticOverrideWarningModalTitle"
        aria-modal="true"
        className="override-warning-modal quill-modal"
        ref={modalRef}
        role="dialog"
        tabIndex={-1}
      >

        <div className="override-warning-modal-header">
          <h3 className="title" id="assignDiagnosticOverrideWarningModalTitle">Are you sure you want to reassign this diagnostic?</h3>
          <button aria-label="Close" className="close-button focus-on-light" onClick={handleCloseModal} type="button">
            <img alt="" src={closeIconSrc} />
          </button>
        </div>

        <div className="override-warning-modal-body modal-body">
          <p>{studentCountElement} {haveOrHas} already been assigned the {diagnosticName}. If they have already completed the diagnostic, they can take it again, but their existing diagnostic data will be overwritten.</p>
          <p>Instead of reassigning, Quill recommends adding new students by editing the existing activity {multiplePacks ? 'packs' : 'pack'} containing the {diagnosticName}.</p>
        </div>

        <div className="override-warning-modal-footer">
          <div className="edit-activity-pack-links">
            {existingPacks.map(pack => (
              <a className="quill-button outlined medium grey focus-on-light" href={editActivityPackHref(pack.unitId)} key={pack.unitId} rel="noopener noreferrer" target="_blank">
                {multiplePacks ? `Edit ${pack.classroomName}’s activity pack` : 'Edit activity pack'}
              </a>
            ))}
          </div>
          <button className="quill-button contained medium green focus-on-light" onClick={handleClickAssign} type="button">Reassign diagnostic</button>
        </div>

      </div>
    </div>
  )

}

export default OverrideWarningModal
